import { Bone, Quaternion, Vector3 } from "three";

import { Joint } from "./joint";
import { Edge } from "./edge";
import { Basis } from "../misc/util";
import { PointType } from ".";
import { EdgeStructure, Vector3FromEdgePoint } from "./metadata";

const boneWildcard = "*";
const rootJointName = "ROOT";
const startPos = new Vector3();
const endPos = new Vector3();

/**
 * This builds an Edge tree (precursor to Armature) by parsing an EdgeStructure
 * object - which encodes the Armature structure (what Edges connect to what)
 * and how that ties into the three.js Model Bones' positions, etc.
 *
 * Note that the naming convention of the Joints is leading-edge (see below), and
 * that this builder can be thought of as an extension of the LeadingEdgeBuilder
 * below but with an extra mapping from IK -> GLTF skeletons (the other builders
 * just traverse the GLTF structure and make a corresponding Edge tree).
 *
 * @param rootBone  root Bone of the three.js skeleton
 * @param structure object defining the IK -> GLTF mappings
 * @param leafBones list of bones that shouldn't get flattened in Actor.buildArmature
 * @returns         root Edge of Edge tree
 */
export function EdgeStructureTreeBuilder(
  rootBone: Bone,
  structure: EdgeStructure,
  leafBones: Bone[],
  addBones = true
): Edge | undefined {
  const jointDict: { [key: string]: Joint } = {};

  let root: Edge | undefined = undefined;
  for (const name in structure) {
    const entry = structure[name];

    let start: Joint | undefined = undefined;
    let end: Joint | undefined = undefined;

    if (entry.start && !entry.parent) {
      if (root) {
        console.error(`${name} one of multiple roots found`);
        return undefined;
      }

      Vector3FromEdgePoint(startPos, rootBone, entry.start);
      Vector3FromEdgePoint(endPos, rootBone, entry.end);

      start = new Joint(startPos, rootJointName);
      end = new Joint(endPos, name);
    } else if (entry.parent) {
      const parentJoint = jointDict[entry.parent];
      // TODO: support out of order structure arrays
      if (!parentJoint) {
        console.error(
          `parent joint not found ${entry.parent}` +
            " - does the parent precede the child?"
        );
        return undefined;
      }

      Vector3FromEdgePoint(endPos, rootBone, entry.end);

      start = parentJoint;
      end = new Joint(endPos, name);
    } else {
      console.error(`invalid non-root edge entry ${name} missing parent`);
      return undefined;
    }

    if (!start || !end) {
      console.error("joint parsing for builder failed");
      return undefined;
    }

    jointDict[rootJointName] = start;
    jointDict[name] = end;

    const edge = new Edge(start, end);

    if (addBones) {
      let prevBone: Bone | undefined = undefined;

      for (const boneName of entry.bones) {
        // NB: at the callsite of this function we flatten the structure of the bones
        //     (so that everything is in world space)
        if (boneName == boneWildcard && prevBone) {
          leafBones.push(prevBone);
          continue;
        }

        const bone = rootBone.getObjectByName(boneName) as Bone;
        if (!bone || !bone.isBone) {
          console.error(`unable to find bone ${boneName} in skeleton`);
          return undefined;
        }

        prevBone = bone;

        const posOffset = !entry.flags?.includes("disablePosOffset");
        const qOffset = !entry.flags?.includes("disableRotOffset");
        const localSpace = entry.flags?.includes("localSpace");

        edge.addBone(bone, qOffset, posOffset, localSpace);
      }
    }

    if (entry.start && !entry.parent) {
      root = edge;
    }
  }

  // TODO: remove me - useful for debugging bone ownership
  // if (root)
  // {
  //   for (let toVisit: Edge[] = [root]; toVisit.length > 0;)
  //   {
  //     const current = toVisit.pop();
  //     if (current)
  //     {
  //       console.log(`${current.name} has:`);
  //       for (let eBone of current.bones)
  //         console.log(`    ${eBone.bone.name}`);
  //     }

  //     if (current?.end.childEdges)
  //     {
  //       for (let child of current?.end.childEdges)
  //         toVisit.push(child);
  //     }
  //   }
  // }

  return root;
}

const avEndPos = new Vector3();
const roundPrecision = 1e6;

/**
 * Traverses a three.js Bone tree and creates a fully connected (no floating
 * edges are allowed) corresponding Edge tree.
 *
 * Note that Bones in three.js (and in GLTF in general) have no end point, just
 * a position and a direction. Therefore if you're parsing a GLTF / three.js
 * skeleton and converting it into an Armature to be used by an IK algorithm,
 * you have to make some assumptions about where the end of the Bone is.
 *
 * In this builder, for Bones with multiple children, we build a Hub Joint at
 * the end of the Edge (which encapsulates the Bone with extra IK metadata),
 * which is located at the centroid of the position of all the child Bones.
 *
 * Leading Edge refers to the naming convention mainly - the end Joint of the
 * Edge will take the Edge's name (and encapsulated Bone's name). So the R|LShoulder
 * Bone (and Edge) goes from the Neck to the Shoulder. So for a Hub Joint
 * (Bone end with many children), you don't have to make names up, the name of
 * the Joint is just the name of the preceding Bone:
 *
 * b: Bone.name
 * e: Edge.name
 * j: Joint.name
 *                         j: B  x
 *                              /
 *                             / b: B
 *                            /  e: B
 *   j: Root            j: A /
 *     x ----------------- x
 *             b: A          \
 *             e: A           \  b: C
 *                             \ e: C
 *                              \
 *                         j: C  x
 *
 * Function is recursive.
 *
 * @param bone  root Bone of three.js skeleton
 * @param start when recursing, this passes down the parent's end Joint
 *              (if undefined, then we create a start Joint)
 * @returns     root Edge of Edge tree
 */
export function LeadingEdgeTreeBuilder(
  bone: Bone,
  start?: Joint,
  addBones = true
): Edge {
  let edge: Edge;
  // check that all children are connected
  if (!childrenConnected(bone, endPos, avEndPos)) {
    // TODO: work out why blender all of a sudden doesn't connect bones

    // round off floating point error from blender
    avEndPos
      .addScalar(Number.EPSILON)
      .multiplyScalar(roundPrecision)
      .round()
      .divideScalar(roundPrecision);

    console.debug(
      `unconnected bones ${bone.name} not supported for ` +
        "leading edge trees, taking truncated average",
      avEndPos
    );
    endPos.copy(avEndPos);
  }

  if (!start) {
    // this is the root joint - we have to create it
    bone.getWorldPosition(startPos);
    start = new Joint(startPos, rootJointName);
  }
  const end = new Joint(endPos, bone.name);
  edge = new Edge(start, end);

  if (addBones) {
    edge.addBone(bone);
  }

  for (const child of bone.children) {
    if (child.type == "Bone") {
      LeadingEdgeTreeBuilder(child as Bone, edge.end);
    }
  }

  return edge;
}

const lengthlessLength = 0.2;

const tempStart = new Vector3();
const tempEnd = new Vector3();

const basis = new Basis();
const boneQ = new Quaternion();

/**
 * WIP builder for Trailing Edge tree. Traverses a three.js Bone tree and creates
 * a corresponding Edge tree.
 *
 * Similar to above, but the start Joint of the Edge is what takes the Edge/Bone's
 * name. This builder supports unconnected bones (we add dummy Edges with no Bone
 * if we see a Hub Joint where children Bones have different positions).
 *
 * b: Bone.name
 * e: Edge.name
 * j: Joint.name
 *                     j: B-end  x
 *                              /
 *                             / b: B
 *                            /  e: B
 *   j: A           j: A-end /
 *     x ----------------- x
 *             b: A          \
 *             e: A           \  b: C
 *                             \ e: C
 *                              \
 *                     j: C-end  x
 *
 * Function is recursive.
 *
 * @param bone      root Bone
 * @param parentEnd end Joint of parent Edge - used when recursing
 * @returns         root Edge of Edge tree
 */
export function TrailingEdgeTreeBuilder(
  bone: Bone,
  parentEnd?: Joint
): Edge | undefined {
  let start: Joint;

  // add dummy edge (for unconnected hubs [kabsch])
  bone.getWorldPosition(tempStart);
  if (parentEnd && parentEnd.pos.equals(tempStart)) {
    start = parentEnd;
    start.name = bone.name;
  } else {
    start = new Joint(tempStart, bone.name);
    if (parentEnd) {
      parentEnd.props.type = PointType.KABSCH;
      const dummy = new Edge(parentEnd, start);
    }
  }

  const children = bone.children.filter((b) => b.type == "Bone");

  // find end
  if (children.length == 1) {
    children[0].getWorldPosition(tempEnd);
  } else if (children.length == 0 || !childrenConnected(bone, tempEnd)) {
    // end needs creating
    bone.getWorldQuaternion(boneQ);
    basis.setFromQuaternion(boneQ);
    tempEnd.copy(basis.right).setLength(lengthlessLength).add(start.pos);
  } else {
    console.error("connected hubs not supported for trailing edge trees");
    return undefined;
  }

  const end = new Joint(tempEnd, `${bone.name}-end`);
  const edge = new Edge(start, end);
  edge.addBone(bone);

  for (const child of children) {
    TrailingEdgeTreeBuilder(child as Bone, end);
  }

  return edge;
}

const tempP = new Vector3();
const tempA = new Vector3();

/**
 * Test to see if Bone.children are connected (have the same position). Writes centroid
 * to `avPos` if passed in. Writes the shared position of the children to `sharedPos` -
 * used if the children are connected.
 *
 * @param bone      Bone under test
 * @param sharedPos Shared position of children
 * @param avPos     Centroid of children (same as sharedPos if connected)
 * @returns         are children connected
 */
function childrenConnected(bone: Bone, sharedPos: Vector3, avPos?: Vector3) {
  let connected = true;
  let cnt = 0;

  tempA.set(0, 0, 0);

  for (const child of bone.children) {
    if (child.type == "Bone") {
      child.getWorldPosition(tempP);
      tempA.add(tempP);
      if (cnt > 0 && !tempP.equals(sharedPos)) {
        console.debug(`${child.name} is unconnected`, tempP, "!=", sharedPos);
        connected = false;
      }

      sharedPos.copy(tempP);
      cnt++;
    }
  }

  if (avPos) {
    avPos.copy(tempA).divideScalar(cnt);
  }

  return connected;
}
